import React from "react";
import Image from "next/image";
import Link from "next/link";
import { BadgeCheck, Crown, Headset } from "lucide-react";

const TRUST_POINTS = [
  { label: "Premium Service", Icon: Crown },
  { label: "24/7 Support", Icon: Headset },
  { label: "Verified Listings", Icon: BadgeCheck },
];

interface IWhyBestCardProps {
  icon: string;
  name: string;
  details: string;
  index: number;
  url?: string;
  disabled?: boolean;
}

function WhyBestCard({
  icon,
  name,
  details,
  index,
  url,
  disabled,
}: IWhyBestCardProps) {
  const cardContent = (
    <div
      className="group relative"
      style={{ animationDelay: `${index * 150}ms` }}
    >
      {/* Main card with glass morphism effect */}
      <div
        className="relative h-full bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-gray-200/50
                    transition-all duration-700 ease-out
                    hover:bg-white hover:border-brand-500/30 hover:shadow-2xl hover:shadow-brand-500/10 hover:-translate-y-2"
      >
        {/* Floating gradient orb background */}
        <div
          className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-brand-500/20 via-brand-400/10 to-transparent
                      rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        />
        {/* Top accent bar */}
        <div
          className="absolute top-0 left-8 right-8 h-[3px] bg-gradient-to-r from-transparent via-brand-500 to-transparent
                      scale-x-0 group-hover:scale-x-100 transition-transform duration-500 rounded-full"
        />
        {/* Icon section */}
        <div className="relative mb-8 flex items-center gap-4">
          <div className="relative">
            {/* Animated ring */}
            <div
              className="absolute inset-0 rounded-2xl border-2 border-brand-500/20 scale-100 group-hover:scale-110
                          transition-transform duration-500"
            />
            <div
              className="absolute inset-0 rounded-2xl bg-gradient-to-br from-pink-500/10 to-transparent
                          group-hover:rotate-180 transition-transform duration-1000"
            />
            {/* Icon container */}
            <div
              className="relative w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-gray-50 to-white
                          group-hover:from-pink-50 group-hover:to-white transition-all duration-500"
            >
              <Image
                className="w-8 h-10 rounded-xl"
                src={icon}
                alt=""
                width={32}
                height={40}
              />
            </div>
          </div>
          {/* Number badge */}
          <div className="text-6xl font-bold text-ink-faint group-hover:text-brand-500/10 transition-colors duration-500">
            {String(index + 1).padStart(2, "0")}
          </div>
        </div>
        {/* Content */}
        <div className="relative space-y-4">
          <h3 className="text-lg font-semibold text-ink-black mb-3 leading-snug">
            {name}
          </h3>
          <p className="text-sm text-ink-secondary leading-relaxed">
            {details}
          </p>
        </div>
        {/* Hover indicator arrow */}
        <div
          className="absolute bottom-8 right-8 w-8 h-8 flex items-center justify-center
                      opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0
                      transition-all duration-500"
        >
          <svg
            className="w-5 h-5 text-brand-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            />
          </svg>
        </div>
      </div>
    </div>
  );
  if (url && !disabled) {
    return (
      <Link
        href={url}
        tabIndex={0}
        className="rounded-3xl focus:outline-none focus:ring-0"
      >
        {cardContent}
      </Link>
    );
  }
  return cardContent;
}

interface IBenefit {
  icon: string;
  name: string;
  details: string;
  url?: string;
  disabled?: boolean;
}

export default function WhyBestSection() {
  const BenefitsList: IBenefit[] = [
    {
      icon: "/images/best-section-bd-location.png",
      name: "Reliable Stays Across Bangladesh",
      details:
        "From cozy rooms to furnished apartments, find sit for short-term stays in Dhaka, Chattogram, Cox's Bazar, Sylhet, and more, with real-time support and verified listings.",
      url: "/reliable-stays-across-bangladesh",
      disabled: false,
    },
    {
      icon: "/images/best-section-people.png",
      name: "Strong Community Support System",
      details:
        "Whether you're a host or a guest, our local team is just a call or message away – 24 hours × 7 days a week. Real help, real people, every time.",
      url: "",
      disabled: true,
    },
    {
      icon: "/images/best-section-calander.png",
      name: "Easy to Use App for Booking & Hosting",
      details:
        "Search, book, list, or manage – all through one simple app. Travela makes travel and hosting seamless for everyone.",
      url: "",
      disabled: true,
    },
    {
      icon: "/images/best-section-money.png",
      name: "Earn or Save More, Without Extra Hassle",
      details:
        "Guests get quality stays at competitive rates. Hosts enjoy personalized support and faster bookings – no need to chase or guess.",
      url: "/earn-by-hosting",
      disabled: false,
    },
  ];

  return (
    <section className="relative bg-white py-20 md:py-32 overflow-hidden">
      {/* Animated background gradient meshes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-pink-500/5 via-pink-300/5 to-transparent
                        rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "8s" }}
        />
        <div
          className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-gradient-to-tr from-pink-500/5 via-pink-300/5 to-transparent
                        rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "10s" }}
        />

        {/* Dotted pattern overlay */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(236, 72, 153, 0.03) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header section */}
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-block mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/10 text-brand-500 rounded-full text-sm font-medium">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Why Choose Us
            </span>
          </div>

          <h2 className="text-2xl md:text-4xl font-bold text-ink-black  mb-6">
            Why Travela is Your{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-400 to-brand-500">
              Best Choice?
            </span>
          </h2>

          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-brand-500 to-transparent mx-auto" />
        </div>

        {/* Cards grid with better spacing */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {BenefitsList.map((benefit, idx) => (
            <WhyBestCard key={idx} {...benefit} index={idx} />
          ))}
        </div>

        {/* Bottom section */}
        <div className="text-center mt-20">
          <p className="text-ink-gray text-base font-medium">
            Join thousands of satisfied travelers and hosts across Bangladesh
          </p>
          {/* Each promise with its mark in a soft brand badge. */}
          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {TRUST_POINTS.map(({ label, Icon }) => (
              <li key={label} className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 ring-1 ring-brand-100">
                  <Icon size={18} strokeWidth={1.75} className="text-brand" />
                </span>
                <span className="text-sm font-medium text-ink-dark">
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
